import React, { useState, useEffect } from 'react';
import { Building2, Search, Plus, Edit2, Trash2, X as XIcon, TrendingUp, Calendar, CheckCircle, XCircle, Clock } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL || '';

const getAuthHeader = () => {
  const token = localStorage.getItem('adminToken');
  return { Authorization: `Bearer ${token}` };
};

const AgencyManager = () => {
  const [agencies, setAgencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingAgency, setEditingAgency] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [locationFilter, setLocationFilter] = useState('all');
  const [locations, setLocations] = useState([]);
  const [selectedAgency, setSelectedAgency] = useState(null);
  const [agencyBookings, setAgencyBookings] = useState([]);
  const [showBookingsModal, setShowBookingsModal] = useState(false);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [approvingAgency, setApprovingAgency] = useState(null);
  const [approvalData, setApprovalData] = useState({
    tier: 'tier3',
    creditLimit: 10000,
    reportingPrice: 3000,
    agencyType: 'freeagent'
  });
  const [formData, setFormData] = useState({
    agencyCode: '',
    agencyName: '',
    contactPerson: '',
    email: '',
    phone: '',
    address: '',
    reportingPrice: 3000,
    isActive: true,
    notes: ''
  });

  useEffect(() => {
    fetchAgencies();
    fetchLocations();
  }, []);

  const fetchLocations = async () => {
    try {
      const response = await axios.get(`${API}/api/locations`);
      setLocations(response.data);
    } catch (error) {
      console.error('Failed to fetch locations:', error);
    }
  };

  const fetchAgencies = async () => {
    try {
      const response = await axios.get(`${API}/api/admin/agencies`, {
        headers: getAuthHeader()
      });
      setAgencies(response.data);
    } catch (error) {
      console.error('Failed to fetch agencies:', error);
      alert('Failed to fetch agencies');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (agency = null) => {
    if (agency) {
      setEditingAgency(agency);
      setFormData({
        agencyCode: agency.agencyCode,
        agencyName: agency.agencyName,
        contactPerson: agency.contactPerson,
        email: agency.email,
        phone: agency.phone,
        address: agency.address || '',
        reportingPrice: agency.reportingPrice || 3000,
        isActive: agency.isActive,
        notes: agency.notes || ''
      });
    } else {
      setEditingAgency(null);
      setFormData({
        agencyCode: '',
        agencyName: '',
        contactPerson: '',
        email: '',
        phone: '',
        address: '',
        reportingPrice: 3000,
        isActive: true,
        notes: ''
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingAgency(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.agencyCode || !formData.agencyName || !formData.contactPerson || !formData.email || !formData.phone) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      if (editingAgency) {
        await axios.put(
          `${API}/api/admin/agencies/${editingAgency._id}`,
          formData,
          { headers: getAuthHeader() }
        );
        alert('Agency updated successfully');
      } else {
        await axios.post(
          `${API}/api/admin/agencies`,
          formData,
          { headers: getAuthHeader() }
        );
        alert('Agency created successfully');
      }
      
      handleCloseModal();
      fetchAgencies();
    } catch (error) {
      alert('Failed to save agency: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleDeleteAgency = async (agencyId) => {
    if (!window.confirm('Are you sure you want to delete this agency?')) {
      return;
    }

    try {
      await axios.delete(`${API}/api/admin/agencies/${agencyId}`, {
        headers: getAuthHeader()
      });
      alert('Agency deleted successfully');
      fetchAgencies();
    } catch (error) {
      alert('Failed to delete agency: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleViewBookings = async (agency) => {
    setSelectedAgency(agency);
    setShowBookingsModal(true);
    
    try {
      const response = await axios.get(`${API}/api/admin/agencies/${agency._id}/bookings`, {
        headers: getAuthHeader()
      });
      setAgencyBookings(response.data);
    } catch (error) {
      console.error('Failed to fetch agency bookings:', error);
      alert('Failed to fetch bookings');
    }
  };

  const handleOpenApprovalModal = (agency) => {
    setApprovingAgency(agency);
    setApprovalData({
      tier: 'tier3',
      creditLimit: 10000,
      reportingPrice: 3000,
      agencyType: agency.agencyType || 'freeagent'
    });
    setShowApprovalModal(true);
  };

  const handleApproveAgent = async () => {
    if (!approvingAgency) return;

    try {
      await axios.post(
        `${API}/api/admin/agencies/${approvingAgency._id}/approve`,
        approvalData,
        { headers: getAuthHeader() }
      );
      alert('Agent approved successfully!');
      setShowApprovalModal(false);
      setApprovingAgency(null);
      fetchAgencies();
    } catch (error) {
      alert('Failed to approve agent: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleRejectAgent = async (agencyId) => {
    const reason = prompt('Enter rejection reason:');
    if (!reason) return;

    try {
      await axios.post(
        `${API}/api/admin/agencies/${agencyId}/reject`,
        { reason },
        { headers: getAuthHeader() }
      );
      alert('Agent rejected');
      fetchAgencies();
    } catch (error) {
      alert('Failed to reject agent: ' + (error.response?.data?.error || error.message));
    }
  };

  const filteredAgencies = agencies.filter(agency => {
    const matchesSearch = 
      agency.agencyName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      agency.agencyCode.toLowerCase().includes(searchTerm.toLowerCase()) ||
      agency.contactPerson.toLowerCase().includes(searchTerm.toLowerCase()) ||
      agency.email.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesLocation = 
      locationFilter === 'all' || 
      agency.locationId === locationFilter;
    
    return matchesSearch && matchesLocation;
  });

  if (loading) {
    return <div className="text-white text-center py-12">Loading agencies...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-black text-white mb-2">Agency Manager</h1>
          <p className="text-gray-400">Manage agency partners and their codes</p>
        </div>
        <button
          onClick={() => handleOpenModal()}
          className="flex items-center space-x-2 px-6 py-3 bg-yellow-400 hover:bg-yellow-500 text-black font-bold rounded-lg transition-colors"
        >
          <Plus size={20} />
          <span>New Agency</span>
        </button>
      </div>

      {/* Search and Filters */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="relative md:col-span-2">
          <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="text"
            placeholder="Search by name, code, contact person, or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-12 pr-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-yellow-400"
          />
        </div>
        
        {/* Location Filter */}
        <div>
          <select
            value={locationFilter}
            onChange={(e) => setLocationFilter(e.target.value)}
            className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-yellow-400"
          >
            <option value="all">All Locations</option>
            {locations.map((location) => (
              <option key={location._id} value={location._id}>
                {location.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="bg-gray-800 border border-gray-700 rounded-xl p-4">
          <p className="text-gray-400 text-sm">Total Agencies</p>
          <p className="text-2xl font-bold text-white">{agencies.length}</p>
        </div>
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4">
          <p className="text-yellow-400 text-sm">Pending Approval</p>
          <p className="text-2xl font-bold text-yellow-400">
            {agencies.filter(a => a.approvalStatus === 'pending').length}
          </p>
        </div>
        <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4">
          <p className="text-green-400 text-sm">Approved & Active</p>
          <p className="text-2xl font-bold text-green-400">
            {agencies.filter(a => a.approvalStatus === 'approved' && a.isActive).length}
          </p>
        </div>
        <div className="bg-purple-500/10 border border-purple-500/30 rounded-xl p-4">
          <p className="text-purple-400 text-sm">Total Bookings</p>
          <p className="text-2xl font-bold text-purple-400">
            {agencies.reduce((sum, a) => sum + (a.totalBookings || 0), 0)}
          </p>
        </div>
      </div>

      {/* Pending Approvals Section */}
      {agencies.filter(a => a.approvalStatus === 'pending').length > 0 && (
        <div className="bg-yellow-500/10 border-2 border-yellow-500/30 rounded-xl p-6">
          <h2 className="text-xl font-bold text-yellow-400 mb-4 flex items-center">
            <Clock className="mr-2" size={24} />
            Pending Agent Registrations ({agencies.filter(a => a.approvalStatus === 'pending').length})
          </h2>
          <div className="space-y-3">
            {agencies.filter(a => a.approvalStatus === 'pending').map((agency) => (
              <div key={agency._id} className="bg-gray-800 border border-gray-700 rounded-lg p-4 flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-start space-x-4">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <p className="text-white font-bold text-lg">{agency.agencyName}</p>
                        <span className="px-2 py-1 bg-blue-500/20 text-blue-400 rounded text-xs font-bold uppercase">
                          {agency.agencyType || 'Free Agent'}
                        </span>
                      </div>
                      <p className="text-gray-400 text-sm">{agency.contactPerson}</p>
                      <div className="grid grid-cols-2 gap-4 mt-3 text-sm">
                        <div>
                          <p className="text-gray-500">Email</p>
                          <p className="text-white">{agency.email}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Phone</p>
                          <p className="text-white">{agency.phone}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Location</p>
                          <p className="text-white">{agency.locationName || 'Not specified'}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Registered</p>
                          <p className="text-white">{new Date(agency.createdAt).toLocaleDateString()}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex space-x-2 ml-4">
                  <button
                    onClick={() => handleOpenApprovalModal(agency)}
                    className="flex items-center space-x-2 px-4 py-2 bg-green-500 hover:bg-green-600 text-white font-bold rounded-lg transition-colors"
                    title="Approve Agent"
                  >
                    <CheckCircle size={18} />
                    <span>Approve</span>
                  </button>
                  <button
                    onClick={() => handleRejectAgent(agency._id)}
                    className="flex items-center space-x-2 px-4 py-2 bg-red-500 hover:bg-red-600 text-white font-bold rounded-lg transition-colors"
                    title="Reject Agent"
                  >
                    <XCircle size={18} />
                    <span>Reject</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Agencies Table */}
      <div className="bg-gray-800 border border-gray-700 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-900">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Code</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Agency Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Contact Person</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Email & Phone</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Tier/Credit</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Bookings</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Reporting Price</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Approval</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {filteredAgencies.map((agency) => (
                <tr key={agency._id} className="hover:bg-gray-700/50">
                  <td className="px-4 py-4">
                    <div className="text-yellow-400 font-bold">{agency.agencyCode}</div>
                  </td>
                  <td className="px-4 py-4">
                    <div className="text-white font-medium">{agency.agencyName}</div>
                    <span className="px-2 py-1 bg-blue-500/20 text-blue-400 rounded text-xs font-bold uppercase mt-1 inline-block">
                      {agency.agencyType || 'Free Agent'}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <div className="text-white">{agency.contactPerson}</div>
                  </td>
                  <td className="px-4 py-4">
                    <div className="text-white text-sm">{agency.email}</div>
                    <div className="text-gray-400 text-sm">{agency.phone}</div>
                  </td>
                  <td className="px-4 py-4">
                    <div className="text-white font-bold">{agency.tier?.toUpperCase() || 'N/A'}</div>
                    <div className="text-gray-400 text-sm">₹{(agency.creditLimit || 0).toLocaleString()}</div>
                  </td>
                  <td className="px-4 py-4">
                    <button
                      onClick={() => handleViewBookings(agency)}
                      className="text-blue-400 hover:text-blue-300 font-medium"
                    >
                      {agency.totalBookings || 0}
                    </button>
                  </td>
                  <td className="px-4 py-4 text-white">₹{(agency.reportingPrice || 3000).toLocaleString()}</td>
                  <td className="px-4 py-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                      agency.approvalStatus === 'approved' ? 'bg-green-500/20 text-green-400' :
                      agency.approvalStatus === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                      'bg-red-500/20 text-red-400'
                    }`}>
                      {agency.approvalStatus}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                      agency.isActive ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'
                    }`}>
                      {agency.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleOpenModal(agency)}
                        className="p-2 hover:bg-blue-500/20 rounded-lg text-blue-400 transition-colors"
                        title="Edit Agency"
                      >
                        <Edit2 size={16} />
                      </button>
                      <button
                        onClick={() => handleDeleteAgency(agency._id)}
                        className="p-2 hover:bg-red-500/20 rounded-lg text-red-400 transition-colors"
                        title="Delete Agency"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {filteredAgencies.length === 0 && (
        <div className="text-center py-12">
          <Building2 className="mx-auto mb-4 text-gray-600" size={48} />
          <p className="text-gray-400">No agencies found</p>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-gray-800 rounded-xl max-w-2xl w-full my-8">
            <div className="p-6 border-b border-gray-700 flex items-center justify-between">
              <h2 className="text-2xl font-bold text-white">
                {editingAgency ? 'Edit Agency' : 'Create New Agency'}
              </h2>
              <button
                onClick={handleCloseModal}
                className="p-2 hover:bg-gray-700 rounded-lg text-gray-400 hover:text-white transition-colors"
              >
                <XIcon size={20} />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Agency Code */}
                <div>
                  <label className="text-white font-medium mb-2 block">Agency Code *</label>
                  <input
                    type="text"
                    value={formData.agencyCode}
                    onChange={(e) => setFormData({ ...formData, agencyCode: e.target.value.toUpperCase() })}
                    required
                    disabled={!!editingAgency}
                    placeholder="e.g., AG001"
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400 disabled:opacity-50 uppercase"
                  />
                  <p className="text-xs text-gray-400 mt-1">Unique code for agency identification</p>
                </div>

                {/* Agency Name */}
                <div>
                  <label className="text-white font-medium mb-2 block">Agency Name *</label>
                  <input
                    type="text"
                    value={formData.agencyName}
                    onChange={(e) => setFormData({ ...formData, agencyName: e.target.value })}
                    required
                    placeholder="e.g., Adventure Tours Pvt Ltd"
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400"
                  />
                </div>

                {/* Contact Person */}
                <div>
                  <label className="text-white font-medium mb-2 block">Contact Person *</label>
                  <input
                    type="text"
                    value={formData.contactPerson}
                    onChange={(e) => setFormData({ ...formData, contactPerson: e.target.value })}
                    required
                    placeholder="e.g., John Doe"
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400"
                  />
                </div>

                {/* Email */}
                <div>
                  <label className="text-white font-medium mb-2 block">Email *</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    placeholder="contact@agency.com"
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400"
                  />
                </div>

                {/* Phone */}
                <div>
                  <label className="text-white font-medium mb-2 block">Phone *</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    required
                    placeholder="+91 9876543210"
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400"
                  />
                </div>

                {/* Reporting Price */}
                <div>
                  <label className="text-white font-medium mb-2 block">Reporting Price (₹)</label>
                  <input
                    type="number"
                    value={formData.reportingPrice}
                    onChange={(e) => setFormData({ ...formData, reportingPrice: parseFloat(e.target.value) })}
                    min="0"
                    step="100"
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400"
                  />
                </div>

                {/* Address */}
                <div className="md:col-span-2">
                  <label className="text-white font-medium mb-2 block">Address</label>
                  <textarea
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    rows={2}
                    placeholder="Complete address..."
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400"
                  />
                </div>

                {/* Notes */}
                <div className="md:col-span-2">
                  <label className="text-white font-medium mb-2 block">Notes</label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows={3}
                    placeholder="Additional notes about the agency..."
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:border-yellow-400"
                  />
                </div>

                {/* Active Status */}
                <div className="flex items-center md:col-span-2">
                  <input
                    type="checkbox"
                    id="isActive"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    className="w-5 h-5 rounded border-gray-600 bg-gray-700 text-yellow-400 focus:ring-yellow-400"
                  />
                  <label htmlFor="isActive" className="ml-2 text-white font-medium">Active</label>
                </div>
              </div>

              <div className="flex justify-end space-x-4 pt-4">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-3 bg-yellow-400 hover:bg-yellow-500 text-black font-bold rounded-lg transition-colors"
                >
                  {editingAgency ? 'Update Agency' : 'Create Agency'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Bookings Modal */}
      {showBookingsModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-gray-800 rounded-xl max-w-4xl w-full my-8">
            <div className="p-6 border-b border-gray-700 flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-white">Agency Bookings</h2>
                <p className="text-gray-400 mt-1">
                  {selectedAgency?.agencyName} ({selectedAgency?.agencyCode})
                </p>
              </div>
              <button
                onClick={() => {
                  setShowBookingsModal(false);
                  setSelectedAgency(null);
                  setAgencyBookings([]);
                }}
                className="p-2 hover:bg-gray-700 rounded-lg text-gray-400 hover:text-white transition-colors"
              >
                <XIcon size={20} />
              </button>
            </div>
            <div className="p-6 max-h-[60vh] overflow-y-auto">
              {agencyBookings.length === 0 ? (
                <div className="text-center py-12">
                  <Calendar className="mx-auto mb-4 text-gray-600" size={48} />
                  <p className="text-gray-400">No bookings found for this agency</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {agencyBookings.map((booking) => (
                    <div key={booking._id} className="bg-gray-700 rounded-lg p-4 border border-gray-600">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <p className="text-white font-bold">{booking.bookingId}</p>
                          <p className="text-sm text-gray-400">{booking.customerName}</p>
                        </div>
                        <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                          booking.status === 'confirmed' ? 'bg-green-500/20 text-green-400' :
                          booking.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                          booking.status === 'cancelled' ? 'bg-red-500/20 text-red-400' :
                          'bg-blue-500/20 text-blue-400'
                        }`}>
                          {booking.status}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                        <div>
                          <p className="text-gray-400">Location</p>
                          <p className="text-white">{booking.locationName}</p>
                        </div>
                        <div>
                          <p className="text-gray-400">Date</p>
                          <p className="text-white">{new Date(booking.bookingDate).toLocaleDateString()}</p>
                        </div>
                        <div>
                          <p className="text-gray-400">Jumpers</p>
                          <p className="text-white">{booking.numberOfJumpers}</p>
                        </div>
                        <div>
                          <p className="text-gray-400">Amount</p>
                          <p className="text-white font-bold">₹{booking.totalAmount}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Approval Modal */}
      {showApprovalModal && approvingAgency && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-gray-800 rounded-xl max-w-md w-full my-8 max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-gray-700">
              <h2 className="text-xl font-bold text-white">Approve Agent</h2>
              <p className="text-gray-400 text-sm mt-1">{approvingAgency.agencyName}</p>
            </div>
            <div className="p-4 space-y-3">
              {/* Agency Type */}
              <div>
                <label className="text-white text-sm font-medium mb-1 block">Agency Type</label>
                <select
                  value={approvalData.agencyType}
                  onChange={(e) => setApprovalData({ ...approvalData, agencyType: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:border-yellow-400"
                >
                  <option value="freeagent">Free Agent</option>
                  <option value="shop">Shop</option>
                  <option value="hotel">Hotel</option>
                  <option value="taxi">Taxi</option>
                </select>
              </div>

              {/* Tier Selection */}
              <div>
                <label className="text-white text-sm font-medium mb-1 block">Select Tier</label>
                <select
                  value={approvalData.tier}
                  onChange={(e) => {
                    const tier = e.target.value;
                    const tierCredits = {
                      tier1: 50000,
                      tier2: 30000,
                      tier3: 10000,
                      tier4: 0
                    };
                    setApprovalData({
                      ...approvalData,
                      tier,
                      creditLimit: tierCredits[tier]
                    });
                  }}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:border-yellow-400"
                >
                  <option value="tier4">Tier 4 - No Credit (Direct Payment)</option>
                  <option value="tier3">Tier 3 - ₹10,000 Credit</option>
                  <option value="tier2">Tier 2 - ₹30,000 Credit</option>
                  <option value="tier1">Tier 1 - ₹50,000 Credit</option>
                </select>
              </div>

              {/* Credit Limit - Only show for tier 1-3 */}
              {approvalData.tier !== 'tier4' && (
                <div>
                  <label className="text-white text-sm font-medium mb-1 block">Credit Limit (₹)</label>
                  <input
                    type="number"
                    value={approvalData.creditLimit}
                    onChange={(e) => setApprovalData({ ...approvalData, creditLimit: parseInt(e.target.value) })}
                    min="0"
                    step="1000"
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:border-yellow-400"
                  />
                </div>
              )}

              {/* Reporting Price */}
              <div>
                <label className="text-white text-sm font-medium mb-1 block">Reporting Price per Jumper (₹)</label>
                <input
                  type="number"
                  value={approvalData.reportingPrice}
                  onChange={(e) => setApprovalData({ ...approvalData, reportingPrice: parseInt(e.target.value) })}
                  min="0"
                  step="100"
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:border-yellow-400"
                />
                <p className="text-gray-500 text-xs mt-1">Base price agent pays. They can upsell to customers.</p>
              </div>

              {/* Info Box */}
              <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3">
                <p className="text-blue-400 text-xs">
                  <strong>Reporting Price System:</strong> Agent pays base price per jumper and can upsell at higher price, keeping the difference as earning.
                </p>
              </div>
            </div>
            <div className="p-4 border-t border-gray-700 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowApprovalModal(false);
                  setApprovingAgency(null);
                }}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white text-sm rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleApproveAgent}
                className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white text-sm font-bold rounded-lg transition-colors"
              >
                Approve Agent
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgencyManager;
